import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ROLE_PERMISSIONS, PAGES } from '../interfaces/role-permissions.interface';

@Injectable({
  providedIn: 'root'
})
export class RoleGuard implements CanActivate {
  
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    // Először ellenőrizzük, hogy be van-e jelentkezve a felhasználó
    const isAuthenticated = this.authService.isAuthenticated();
    
    if (!isAuthenticated) {
      // Ha nincs bejelentkezve, irányítsuk át a login oldalra
      if (state.url !== '/' && state.url !== '/login') {
        this.authService.setRedirectUrl(state.url);
      }
      this.router.navigate(['/login']);
      return false;
    }

    // Lekérjük a felhasználó adatait
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser || !currentUser.jobs || currentUser.jobs.length === 0) {
      console.error('Nincs felhasználói adat vagy munkakör információ');
      this.router.navigate(['/overview']);
      return false;
    }

    // Meghatározzuk az aktuális oldal nevét az URL alapján
    const requestedPage = this.getPageFromUrl(state.url);
    
    // Ellenőrizzük a jogosultságot (több role uniója)
    if (this.hasPermission(currentUser.jobs, requestedPage)) {
      return true;
    } else {
      console.warn(`Nincs jogosultság ehhez az oldalhoz: ${requestedPage}. Felhasználó munkakörök: ${currentUser.jobs.join(', ')}`);
      // Átirányítás az overview oldalra, ha nincs jogosultság
      this.router.navigate(['/overview']);
      return false;
    }
  }

  /**
   * URL-ből meghatározza az oldal nevét
   */
  private getPageFromUrl(url: string): string {
    // Eltávolítjuk a kezdő slash-t és a query paramétereket
    const cleanUrl = url.replace(/^\//, '').split('?')[0].split('/')[0];
    
    // Speciális esetek kezelése
    if (cleanUrl.includes('customer-details')) {
      return PAGES.CUSTOMER_DETAILS;
    }
    if (cleanUrl.includes('company-details')) {
      return PAGES.COMPANY_DETAILS;
    }
    
    // Egyéb oldalak
    switch (cleanUrl) {
      case '':
      case 'overview':
        return PAGES.OVERVIEW;
      case 'call-center-database':
        return PAGES.CALL_CENTER_DATABASE;
      case 'contracts':
        return PAGES.CONTRACTS;
      case 'customers':
        return PAGES.CUSTOMERS;
      case 'illustrations':
      case 'add-new-illustration':
        return PAGES.ILLUSTRATIONS;
      case 'summary':
        return PAGES.SUMMARY;
      case 'call-center-stats':
        return PAGES.CALL_CENTER_STATS;
      case 'daily-report':
        return PAGES.DAILY_REPORT;
      case 'admin':
        return PAGES.ADMIN;
      case 'profile':
        return PAGES.PROFILE;
      case 'calendar':
        return PAGES.CALENDAR;
      default:
        console.warn(`Ismeretlen oldal: ${cleanUrl}`);
        return PAGES.OVERVIEW; // Alapértelmezett
    }
  }

  /**
   * Ellenőrzi, hogy a felhasználó munkakörének van-e jogosultsága az adott oldalhoz
   * Több role esetén bármelyik role jogosultsága elegendő
   */
  private hasPermission(userRoles: string[], page: string): boolean {
    for (const role of userRoles) {
      const rolePermissions = ROLE_PERMISSIONS[role];
      if (rolePermissions && rolePermissions.includes(page)) {
        return true;
      }
    }
    
    // Ha nincs definiálva egyik munkakör sem, alapértelmezett jogosultságokat használjunk
    const defaultPermissions = ROLE_PERMISSIONS['default'];
    return defaultPermissions.includes(page);
  }

  /**
   * Publikus metódus a komponensek számára a jogosultság ellenőrzésére
   */
  public checkPermission(page: string): boolean {
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser || !currentUser.jobs || currentUser.jobs.length === 0) {
      return false;
    }
    
    return this.hasPermission(currentUser.jobs, page);
  }

  /**
   * Visszaadja a felhasználó által elérhető oldalakat (összes role uniója)
   */
  public getAccessiblePages(): string[] {
    const currentUser = this.authService.getCurrentUser();
    
    if (!currentUser || !currentUser.jobs || currentUser.jobs.length === 0) {
      return ROLE_PERMISSIONS['default'];
    }
    
    const allPages = new Set<string>();
    for (const role of currentUser.jobs) {
      const rolePages = ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS['default'];
      rolePages.forEach(page => allPages.add(page));
    }
    return Array.from(allPages);
  }
}